import { getOriginChainId } from '@/domain/hooks/useOriginChainId'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets } from '@/ui/assets'
import { hyperEVM, isolatedRegistryAddress } from './constants'
import { ChainConfigEntry, SupportedChainId } from './types'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { usdxlTokenAddress } from '../contracts-generated'
import { incentivizedPairs, isolatedPartnerPoints, pooledPartnerPoints } from './points'
import {
  BUDDY_USDXL_PAIR_ADDRESS,
  CMETH_UETH_PAIR_ADDRESS,
  HFUN_USDXL_PAIR_ADDRESS,
  HWHLP_USDHL_PAIR_ADDRESS,
  HWHLP_USDT0_PAIR_ADDRESS,
  WHLP_USDT0_PAIR_ADDRESS,
  LHYPE_USDT0_PAIR_ADDRESS,
  MHYPE_WHYPE_PAIR_ADDRESS,
  PURR_USDXL_PAIR_ADDRESS,
  SUSDE_USDXL_PAIR_ADDRESS,
  USDXL_LHYPE_PAIR_ADDRESS,
  USOL_USDT0_PAIR_ADDRESS,
  XAUT0_USDT0_PAIR_ADDRESS,
  UPUMP_USDXL_PAIR_ADDRESS,
  PTKHYPE_HYPE_PAIR_ADDRESS,
  PTHWHLP_USDT0_PAIR_ADDRESS,
  HAHYPE_HYPE_PAIR_ADDRESS,
  HBUSDT_USDT0_PAIR_ADDRESS,
  PTVKHYPE_HYPE_PAIR_ADDRESS,
} from '../consts'

const commonTokenSymbolToReplacedName = {
  [TokenSymbol('USDC')]: { name: 'Circle USD', symbol: TokenSymbol('USDC') },
  [TokenSymbol('SolvBTC')]: { name: 'SolvBTC', symbol: TokenSymbol('SolvBTC') },
  [TokenSymbol('wstHYPE')]: { name: 'Staked Hype', symbol: TokenSymbol('wstHYPE') },
  [TokenSymbol('WHYPE')]: { name: 'Hype', symbol: TokenSymbol('HYPE') },
  [TokenSymbol('USDe')]: { name: 'USDe', symbol: TokenSymbol('USDe') },
  [TokenSymbol('mHYPE')]: { name: 'SpinUp mHYPE', symbol: TokenSymbol('mHYPE') },
  [TokenSymbol('PT-HWHLP-18DEC2025')]: { name: 'PT hwHLP', symbol: TokenSymbol('PT-hwHLP') },
  [TokenSymbol('PT-vkHYPE-13NOV2025')]: { name: 'PT vkHYPE', symbol: TokenSymbol('PT-vkHYPE') },
  [TokenSymbol('beHYPE')]: { name: 'Hyperbeat Staked HYPE', symbol: TokenSymbol('beHYPE') },
}

const chainConfig: Record<SupportedChainId, ChainConfigEntry> = {
  [hyperEVM.id]: {
    originChainId: hyperEVM.id,
    meta: {
      name: 'Hyperliquid EVM',
      logo: assets.hyperEvmLogo,
    },
    permitSupport: {},
    tokensWithMalformedApprove: [],
    isolatedPartnerPoints,
    pooledPartnerPoints,
    incentivizedPairs,
    airdrop: {},
    extraTokens: [
      {
        symbol: TokenSymbol('wstHYPE'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x94e8396e0869c9F2200760aF0621aFd240E1CF38'),
      },
      {
        symbol: TokenSymbol('UBTC'),
        oracleType: 'zero-price',
        address: CheckedAddress('0x9fdbda0a5e284c32744d2f17ee5c74b284993463'),
      },
      {
        symbol: TokenSymbol('USDXL'),
        oracleType: 'fixed-usd',
        address: getContractAddress(usdxlTokenAddress, hyperEVM.id),
      },
      {
        symbol: TokenSymbol('UETH'),
        oracleType: 'zero-price',
        address: CheckedAddress('0xBe6727B535545C67d5cAa73dEa54865B92CF7907'),
      },
      {
        symbol: TokenSymbol('stHYPE'),
        oracleType: 'pyth-oracle',
        oracleAddress: CheckedAddress('0x1d0e4ea616a749c1620118f7d97c111e8ec36e8b'),
        address: CheckedAddress('0xfFaa4a3D97fE9107Cef8a3F48c069F577Ff76cC1'),
      },
    ],
    markets: {
      defaultAssetToBorrow: TokenSymbol('WHYPE'),
      nativeAssetInfo: {
        nativeAssetName: 'Hype',
        nativeAssetSymbol: TokenSymbol('HYPE'),
        wrappedNativeAssetSymbol: TokenSymbol('WHYPE'),
        wrappedNativeAssetAddress: CheckedAddress('0x5555555555555555555555555555555555555555'),
        minRemainingNativeAssetBalance: NormalizedUnitNumber(0.001),
      },
      tokenSymbolToReplacedName: {
        ...commonTokenSymbolToReplacedName,
      },
      oracles: {
        [TokenSymbol('WHYPE')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('wstHYPE')]: {
          type: 'underlying-asset',
          asset: TokenSymbol('wstHYPE'),
        },
        [TokenSymbol('UBTC')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('UETH')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USDXL')]: {
          type: 'fixed',
          providedBy: ['hypurrfi'],
        },
        [TokenSymbol('USDe')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('feUSD')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USD₮0')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('USDHL')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('kHYPE')]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
        [TokenSymbol('XAUt0')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [TokenSymbol('PT-kHYPE-13NOV2025')]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
        [TokenSymbol('PT-kHYPE-19MAR2026')]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
        [TokenSymbol('beHYPE')]: {
          type: 'redemption-rate',
          providedBy: ['redstone'],
        },
        [TokenSymbol('USDC')]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
      },
    },
    isolated: {
      registryAddress: isolatedRegistryAddress[hyperEVM.id],
      oracles: {
        [MHYPE_WHYPE_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [CMETH_UETH_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [PURR_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [HFUN_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [USDXL_LHYPE_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [SUSDE_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [BUDDY_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [USOL_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [LHYPE_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [XAUT0_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [HWHLP_USDT0_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [HWHLP_USDHL_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [WHLP_USDT0_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['pyth'],
        },
        [UPUMP_USDXL_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['pyth'],
        },
        [PTKHYPE_HYPE_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
        [PTHWHLP_USDT0_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
        [HAHYPE_HYPE_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['hypurrfi'],
        },
        [HBUSDT_USDT0_PAIR_ADDRESS]: {
          type: 'market-price',
          providedBy: ['hypurrfi'],
        },
        [PTVKHYPE_HYPE_PAIR_ADDRESS]: {
          type: 'redemption-rate',
          providedBy: ['hypurrfi'],
        },
      },
    },
  },
}

export function getChainConfigEntry(chainId: number): ChainConfigEntry {
  const originChainId = getOriginChainId(chainId)
  if (originChainId !== chainId) {
    return {
      ...chainConfig[originChainId],
    }
  }

  return (
    chainConfig[chainId] || {
      originChainId: chainId,
      meta: {
        name: 'Unknown',
        logo: assets.hyperEvmLogo,
      },
      permitSupport: {},
      tokensWithMalformedApprove: [],
      isolatedPartnerPoints: {},
      pooledPartnerPoints: {},
      incentivizedPairs: {},
      airdrop: {},
      extraTokens: [],
      markets: {},
      isolated: {},
    }
  )
}
